"use client";

import { useEffect, useRef, ReactNode } from "react";

import { useEventLocationContext } from "@stadium/services/analytics/context/event-location";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";
import { ANALYTICS_EVENT_ACTION_SCROLLED } from "@stadium/services/analytics/constants/default";

const DEFAULT_THRESHOLDS = [25, 50, 75, 100];
const MIN_DOCUMENT_HEIGHT = 500;

/**
 * ScrollDepthTrackerProps
 * @param thresholds - Array of percentages to track (e.g. [25, 50, 75, 100])
 * @param eventProperties - Additional properties to send with the scroll event
 * @param analyticsTarget - Optional segment for `useWithAnalytics` `target` (e.g. category listing scroll)
 * @param onElement - Optional ref to a specific scrollable element. If not provided, tracks globalthis scroll.
 * @param children - React nodes to render
 */
type ScrollDepthTrackerProps = {
  thresholds?: number[];
  eventProperties?: Record<string, unknown>;
  analyticsTarget?: string;
  onElement?: React.RefObject<HTMLElement | null>;
  children?: ReactNode;
};

/**
 * ScrollDepthTracker tracks scroll depth percentages.
 *
 * IMPORTANT: This component should only be applied to elements that have a scrollbar.
 * If tracking the whole page, do not pass the `onElement` prop.
 */
const ScrollDepthTracker = ({
  thresholds = DEFAULT_THRESHOLDS,
  eventProperties = {},
  analyticsTarget,
  onElement,
  children,
}: ScrollDepthTrackerProps) => {
  const { location } = useEventLocationContext();
  const withAnalytics = useWithAnalytics();
  const trackedDepthsRef = useRef<Set<number>>(new Set());
  const eventPropertiesRef = useRef(eventProperties);
  const analyticsTargetRef = useRef(analyticsTarget);

  // Sync ref with the latest props without triggering effects
  useEffect(() => {
    eventPropertiesRef.current = eventProperties;
  }, [eventProperties]);

  useEffect(() => {
    analyticsTargetRef.current = analyticsTarget;
  }, [analyticsTarget]);

  useEffect(() => {
    // Only attach listener if eventLocation is present, as per requirement
    if (!location) return;

    trackedDepthsRef.current.clear();

    const handleScroll = () => {
      let scrollTop: number;
      let docHeight: number;
      let windowHeight: number;

      if (onElement?.current) {
        const element = onElement.current;
        scrollTop = element.scrollTop;
        docHeight = element.scrollHeight;
        windowHeight = element.clientHeight;
      } else {
        scrollTop = globalThis.scrollY;
        docHeight = document.documentElement.scrollHeight;
        windowHeight = globalThis.innerHeight;
      }

      // Safeguard for global document tracking: ignore if the page is suspiciously short
      if (!onElement?.current && docHeight < MIN_DOCUMENT_HEIGHT) return;

      const totalScrollable = docHeight - windowHeight;
      if (totalScrollable <= 0) return;

      const scrollPercent = Math.round((scrollTop / totalScrollable) * 100);

      thresholds.forEach((threshold) => {
        if (scrollPercent >= threshold && !trackedDepthsRef.current.has(threshold)) {
          trackedDepthsRef.current.add(threshold);

          const target = analyticsTargetRef.current ?? "";

          withAnalytics(undefined, {
            track: true,
            target,
            action: ANALYTICS_EVENT_ACTION_SCROLLED,
            eventProperties: {
              ...eventPropertiesRef.current,
              scroll_depth: threshold,
            },
          })();
        }
      });
    };

    const targetElement = onElement?.current;
    const target = targetElement || globalThis;
    target.addEventListener("scroll", handleScroll, { passive: true });

    // Initial check with a slight delay to allow layout to stabilize (hydration, async content)
    const timeoutId = setTimeout(handleScroll, 500);

    return () => {
      target.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, withAnalytics, thresholds, onElement, onElement?.current]);

  return children;
};

export default ScrollDepthTracker;
